import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  Place as PlaceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  MyLocation as MyLocationIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EventNote as ItineraryIcon,
  LightbulbOutlined as SuggestionIcon,
} from '@mui/icons-material';
import {
  getUniqueTripLocationsForExplore,
  findTripLocationInQuery,
  resolveAllTripLocationCoords,
  filterPlacesNearLocation,
  matchPlaceToTripLocation,
  getLocationsForResultsList,
  centerMapOnCoordinates,
  fitMapToPlaces,
  fitMapToCoordinates,
  isLocationBrowseQuery,
} from '../utils/exploreMapUtils';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import api from '../config/api';
import { fetchBrowsePlaces, getPlacesCapabilities } from '../utils/placesBrowseClient';
import LazyPlacePhoto from './LazyPlacePhoto';
import { bindGoogleMapResize, MIN_EXPLORE_RADIUS_MILES, zoomForRadiusMiles } from '../utils/googleMapLayout';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, documentAddButtonSx, documentEyebrowSx, documentMapBoxSx, documentTextButtonSx } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';

const TripMapExplorerNew = ({ trip, onAddActivity, isCreator, onCreateSuggestion, isActive = true }) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const paneRef = useRef(null);
  const [paneHeight, setPaneHeight] = useState(420);
  const [map, setMap] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showResults, setShowResults] = useState(false);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [addMenuPlace, setAddMenuPlace] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    lodging: true,
    restaurant: true,
    tourist_attraction: true,
    other: true
  });
  const [expandedLocations, setExpandedLocations] = useState({});
  const [activeSearchLocation, setActiveSearchLocation] = useState(null);
  const tripCoordMapRef = useRef(new Map());
  const selectedPredictionRef = useRef(null);
  const [hasGooglePlacesBrowse, setHasGooglePlacesBrowse] = useState(false);

  // Toggle section expansion
  const toggleSection = (category) => {
    setExpandedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Toggle location expansion
  const toggleLocation = (locationName) => {
    setExpandedLocations(prev => ({
      ...prev,
      [locationName]: !prev[locationName]
    }));
  };

  // Unique trip stops (road trips may revisit the same city)
  const getTripLocations = React.useCallback(() => {
    if (!trip) return [];
    return getUniqueTripLocationsForExplore(trip);
  }, [trip]);

  useEffect(() => {
    if (!trip?.id) return undefined;
    let cancelled = false;
    getPlacesCapabilities(trip.id)
      .then((data) => {
        if (!cancelled) {
          setHasGooglePlacesBrowse(Boolean(data.hasGooglePlacesBrowse));
        }
      })
      .catch(() => {
        if (!cancelled) setHasGooglePlacesBrowse(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trip?.id]);

  // Initialize first location as expanded
  useEffect(() => {
    const locations = getTripLocations();
    if (locations.length > 0 && Object.keys(expandedLocations).length === 0) {
      setExpandedLocations({ [locations[0].name]: true });
    }
  }, [getTripLocations, expandedLocations]);

  useEffect(() => {
    if (activeSearchLocation) {
      setExpandedLocations((prev) => ({ ...prev, [activeSearchLocation]: true }));
    }
  }, [activeSearchLocation]);

  // Debounce utility
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Autocomplete search function
  const searchAutocomplete = React.useCallback(async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const center = map?.getCenter?.();
      const params = new URLSearchParams({ q: query });
      if (trip?.id) params.set('tripId', String(trip.id));
      if (center) {
        params.set('lat', String(center.lat()));
        params.set('lng', String(center.lng()));
      }
      const response = await api.get(`/geo/autocomplete?${params.toString()}`);
      const results = (response.data?.results || []).slice(0, 5).map((place) => ({
        place_id: place.placeId,
        description: place.address || place.name,
        structured_formatting: {
          main_text: place.name,
          secondary_text: place.address && place.address !== place.name ? place.address : '',
        },
        coordinates: place.coordinates,
      }));
      setSuggestions(results);
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
      setSuggestions([]);
    }
  }, [map, trip?.id]);

  // Debounced autocomplete search
  const debouncedAutocomplete = React.useMemo(
    () => debounce(searchAutocomplete, 300),
    [searchAutocomplete]
  );

  // Categories for filtering
  const categories = [
    { key: 'all', label: 'All', icon: <PlaceIcon />, color: '#1976d2' },
    { key: 'lodging', label: 'Hotels', icon: <HotelIcon />, color: '#2e7d32' },
    { key: 'restaurant', label: 'Restaurants', icon: <RestaurantIcon />, color: '#ed6c02' },
    { key: 'tourist_attraction', label: 'Attractions', icon: <PlaceIcon />, color: '#9c27b0' }
  ];

  const dedupePlacesById = (places) => {
    const seen = new Set();
    return places.filter((place) => {
      const key = place.placeId || place.id;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const getMarkerIcon = (category) => {
    const icons = {
      lodging: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
      restaurant: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png',
      tourist_attraction: 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png',
      other: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
    };
    return icons[category] || icons.other;
  };

  // Get price level text
  const getPriceLevel = (priceLevel) => {
    const levels = ['Free', '$', '$$', '$$$', '$$$$'];
    return levels[priceLevel] || 'N/A';
  };

  // Add markers to map
  const addMarkersToMap = React.useCallback((places) => {
    // Clear existing markers
    mapMarkers.forEach(marker => marker.setMap(null));
    const newMarkers = [];

    places.forEach(place => {
      if (place.geometry) {
        const marker = new window.google.maps.Marker({
          position: place.geometry,
          map: map,
          title: place.name,
          icon: {
            url: getMarkerIcon(place.category),
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px;">
              <h3 style="margin: 0 0 8px 0; font-size: 16px;">${place.name}</h3>
              <p style="margin: 0 0 4px 0; color: #666;">${place.address}</p>
              <p style="margin: 0; color: #666;">
                ${place.rating ? `⭐ ${Number(place.rating).toFixed(1)}` : ''}${place.priceLevel ? ` | ${getPriceLevel(place.priceLevel)}` : ''}
              </p>
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        newMarkers.push(marker);
      }
    });

    setMapMarkers(newMarkers);
  }, [map, mapMarkers]);

  const mapApiPlace = React.useCallback((place, category, searchLocation) => ({
    id: place.id || place.placeId,
    placeId: place.placeId || place.id,
    name: place.name,
    displayName: place.name,
    rating: place.rating || 0,
    priceLevel: place.priceLevel || 0,
    geometry: place.geometry || null,
    address: place.address || '',
    category: category || place.types?.[0] || 'other',
    types: place.types || [],
    photos: place.photos || [],
    photoName: place.photoName || place.photos?.[0]?.name || null,
    searchLocation,
    source: place.source
  }), []);

  const fetchPlacesForLocations = React.useCallback(async (locationsToSearch, { browse = false, getTextQuery } = {}) => {
    const tripLocations = getTripLocations();
    const coordMap = await resolveAllTripLocationCoords(tripLocations);
    tripCoordMapRef.current = coordMap;
    const allResults = [];
    const useGoogle = hasGooglePlacesBrowse;

    for (const location of locationsToSearch) {
      const query = getTextQuery ? getTextQuery(location) : '';
      const requests = browse
        ? [
            { category: 'lodging' },
            { category: 'restaurant' },
            { category: 'tourist_attraction' },
          ]
        : [{ category: 'tourist_attraction', query }];

      const searchResults = await Promise.all(
        requests.map((request) =>
          fetchBrowsePlaces({
            tripId: trip?.id,
            location: location.name,
            category: request.category,
            google: useGoogle
          })
            .then((result) => ({ ...result, category: request.category }))
            .catch(() => ({ places: [], category: request.category }))
        )
      );

      searchResults.forEach((result) => {
        (result.places || []).forEach((place) => {
          const processed = mapApiPlace(place, result.category, location.name);
          allResults.push({
            ...processed,
            searchLocation: matchPlaceToTripLocation(processed, tripLocations, coordMap),
          });
        });
      });
    }

    const nearbyResults = locationsToSearch.reduce((acc, location) => {
      const coords = coordMap.get(location.name);
      const forLocation = allResults.filter((place) => place.searchLocation === location.name);
      return acc.concat(filterPlacesNearLocation(forLocation, coords, 50));
    }, []);

    return {
      processedResults: dedupePlacesById(nearbyResults),
      coordMap,
    };
  }, [getTripLocations, hasGooglePlacesBrowse, mapApiPlace, trip?.id]);

  // Load initial suggestions when map is ready
  const loadInitialSuggestions = React.useCallback(async (mapInstance) => {
    if (!mapInstance) return;

    const locations = getTripLocations();
    if (locations.length === 0) {
      setError('No trip locations found. Please add locations to your trip first.');
      setIsSearching(false);
      return;
    }

    if (!hasGooglePlacesBrowse) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const { processedResults, coordMap } = await fetchPlacesForLocations(locations, { browse: true });

      setActiveSearchLocation(null);
      setSearchResults(processedResults);
      setShowResults(true);
      addMarkersToMap(processedResults);
      fitMapToCoordinates(mapInstance, Array.from(coordMap.values()));
    } catch (loadError) {
      console.error('Error loading initial suggestions:', loadError);
      setError('Failed to load suggestions');
    } finally {
      setIsSearching(false);
    }
  }, [getTripLocations, addMarkersToMap, fetchPlacesForLocations, hasGooglePlacesBrowse]);

  const createMapInstance = React.useCallback((center) => {
    return new window.google.maps.Map(mapRef.current, {
      center,
      zoom: zoomForRadiusMiles(MIN_EXPLORE_RADIUS_MILES),
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });
  }, []);

  // Initialize map only when component is active
  useEffect(() => {
    if (isActive && mapRef.current && !map) {
      const initMap = async () => {
        try {
          const locations = getTripLocations();

          if (locations.length === 0) {
            setError('No trip locations found. Please add locations to your trip first.');
            return;
          }

          const coordMap = await resolveAllTripLocationCoords(locations);
          tripCoordMapRef.current = coordMap;
          const validCoords = Array.from(coordMap.values());

          if (validCoords.length === 0) {
            setError('Could not find coordinates for your trip locations.');
            return;
          }

          const mapInstance = createMapInstance(validCoords[0]);
          setMap(mapInstance);

          if (validCoords.length > 1) {
            fitMapToCoordinates(mapInstance, validCoords);
          } else {
            mapInstance.setZoom(zoomForRadiusMiles(MIN_EXPLORE_RADIUS_MILES));
          }

          setTimeout(() => {
            loadInitialSuggestions(mapInstance);
          }, 500);
        } catch (initError) {
          console.error('Error initializing map:', initError);
          setError('Failed to initialize map');
        }
      };

      loadGoogleMaps()
        .then(async () => {
          await initMap();
        })
        .catch((loadError) => {
          console.error('Failed to load Google Maps:', loadError);
          setError('Failed to load Google Maps script');
        });
    }
  }, [map, isActive, trip, getTripLocations, loadInitialSuggestions, createMapInstance]);

  // Cleanup map when component becomes inactive
  useEffect(() => {
    if (!isActive && map) {
      // Clear map instance when component becomes inactive
      setMap(null);
    }
  }, [isActive, map]);

  useEffect(() => {
    if (!map || !mapRef.current) return undefined;
    const unbindMap = bindGoogleMapResize(map, mapRef.current);
    const unbindPane = paneRef.current ? bindGoogleMapResize(map, paneRef.current) : () => {};
    return () => {
      unbindMap();
      unbindPane();
    };
  }, [map, paneHeight]);

  useLayoutEffect(() => {
    if (!isActive) return undefined;

    const updatePaneHeight = () => {
      const pane = paneRef.current;
      if (!pane) return;
      const top = pane.getBoundingClientRect().top;
      setPaneHeight(Math.max(280, Math.floor(window.innerHeight - top - 16)));
    };

    const frame = window.requestAnimationFrame(updatePaneHeight);
    const timeout = window.setTimeout(updatePaneHeight, 350);
    window.addEventListener('resize', updatePaneHeight);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      window.removeEventListener('resize', updatePaneHeight);
    };
  }, [isActive, showResults]);

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          if (map) {
            map.setCenter(location);
            map.setZoom(15);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  // Search function — city/autocomplete queries browse hotels/restaurants/attractions
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !map) return;

    setIsSearching(true);
    setError(null);

    try {
      const tripLocations = getTripLocations();
      const targetLocation = findTripLocationInQuery(searchQuery, tripLocations);
      const locationsToSearch = targetLocation ? [targetLocation] : tripLocations;
      const browse = Boolean(targetLocation && isLocationBrowseQuery(searchQuery, targetLocation));

      setActiveSearchLocation(targetLocation?.name || null);

      let results = [];
      let coordMap = tripCoordMapRef.current;

      if (browse) {
        const fetched = await fetchPlacesForLocations(locationsToSearch, { browse: true });
        results = fetched.processedResults;
        coordMap = fetched.coordMap;
      } else if (selectedPredictionRef.current?.place_id || suggestions[0]?.place_id) {
        const selected = selectedPredictionRef.current || suggestions[0];
        const selectedPlaceId = selected.place_id;
        selectedPredictionRef.current = null;
        let lat = selected.coordinates?.lat;
        let lng = selected.coordinates?.lng;
        let name = selected.structured_formatting?.main_text || selected.description;
        let address = selected.description;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          const response = await api.get(`/geo/place/${encodeURIComponent(selectedPlaceId)}`);
          const place = response.data?.place;
          lat = place?.coordinates?.lat;
          lng = place?.coordinates?.lng;
          name = place?.name || name;
          address = place?.address || address;
        }
        if (selectedPlaceId) {
          results = [{
            id: selectedPlaceId,
            placeId: selectedPlaceId,
            name,
            address,
            geometry: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
            category: 'other',
            rating: 0,
            photos: [],
            searchLocation: targetLocation?.name || tripLocations[0]?.name
          }];
        }
      } else {
        const fetched = await fetchPlacesForLocations(locationsToSearch, { browse: true });
        results = fetched.processedResults;
        coordMap = fetched.coordMap;
      }

      if (results.length === 0) {
        setSearchResults([]);
        setShowResults(true);
        mapMarkers.forEach((marker) => marker.setMap(null));
        setMapMarkers([]);
        if (targetLocation) {
          const targetCoords = coordMap.get(targetLocation.name);
          centerMapOnCoordinates(map, targetCoords);
        } else {
          fitMapToCoordinates(map, Array.from(coordMap.values()));
        }
        return;
      }

      setSearchResults(results);
      setShowResults(true);

      if (targetLocation) {
        const targetCoords = coordMap.get(targetLocation.name);
        const cityResults = results.filter(
          (place) => place.searchLocation === targetLocation.name
        );
        addMarkersToMap(cityResults);
        centerMapOnCoordinates(map, targetCoords);
        if (cityResults.length > 0) {
          fitMapToPlaces(map, cityResults);
        }
      } else {
        addMarkersToMap(results);
        fitMapToCoordinates(map, Array.from(coordMap.values()));
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };



  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearchLocation(null);
    setSearchResults([]);
    setShowResults(false);
    mapMarkers.forEach(marker => marker.setMap(null));
    setMapMarkers([]);
    if (map) {
      const locations = getTripLocations();
      resolveAllTripLocationCoords(locations).then((coordMap) => {
        tripCoordMapRef.current = coordMap;
        fitMapToCoordinates(map, Array.from(coordMap.values()));
        loadInitialSuggestions(map);
      });
    }
  };

  // Filter results by category and location
  const getFilteredResultsForLocation = (locationName) => {
    let locationResults = searchResults.filter(place => place.searchLocation === locationName);
    
    if (selectedCategory !== 'all') {
      locationResults = locationResults.filter(place => place.category === selectedCategory);
    }
    
    return locationResults;
  };


  const buildActivityData = (place) => {
    const categoryMap = { restaurants: 'restaurant', hotels: 'hotel', attractions: 'activity' };
    return {
      name: place.name,
      location: place.address,
      google_map_link: place.place_id ? `https://www.google.com/maps/place/?q=place_id:${place.place_id}` : '',
      category: categoryMap[place.category] || place.category || 'other',
      description: place.rating ? `Rating: ${place.rating.toFixed(1)}/5` : '',
    };
  };

  // Handle add button click
  const handleAddClick = (event, place) => {
    if (isCreator) {
      setAddMenuAnchor(event.currentTarget);
      setAddMenuPlace(place);
    } else {
      onCreateSuggestion(buildActivityData(place));
    }
  };

  const handleMenuClose = () => {
    setAddMenuAnchor(null);
    setAddMenuPlace(null);
  };

  const handleAddAsItinerary = () => {
    if (addMenuPlace) {
      onAddActivity(buildActivityData(addMenuPlace));
    }
    handleMenuClose();
  };

  const handleAddAsSuggestion = () => {
    if (addMenuPlace) {
      onCreateSuggestion(buildActivityData(addMenuPlace));
    }
    handleMenuClose();
  };


  const renderResults = () => {
    const locations = getLocationsForResultsList(getTripLocations(), activeSearchLocation);

    // If no locations, show error message
    if (locations.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            No Trip Locations Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please add locations to your trip to explore places.
          </Typography>
        </Box>
      );
    }

    // Always show location-based sections (single or multiple)
    return (
      <Box>
        {locations.map((location, locationIndex) => (
          <Accordion 
            key={`${location.name}-${locationIndex}`}
            expanded={expandedLocations[location.name] || false}
            onChange={() => toggleLocation(location.name)}
            elevation={0}
            sx={{ mb: 1, bgcolor: 'transparent', '&:before': { display: 'none' }, boxShadow: 'none' }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: DOCUMENT.ink3 }} />}
              sx={{
                bgcolor: 'transparent',
                color: DOCUMENT.ink,
                px: 0,
                minHeight: 48,
                '&:hover': { bgcolor: 'transparent' },
                '& .MuiAccordionSummary-content': { my: 1 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>
                  {location.name}
                </Typography>
                <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {`${getFilteredResultsForLocation(location.name).length} places`}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderCategorySections(location.name)}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  // Render category sections
  const renderCategorySections = (locationName) => {
    const categoryOrder = ['lodging', 'restaurant', 'tourist_attraction', 'other'];
    const categoryLabels = {
      lodging: 'Hotels',
      restaurant: 'Restaurants', 
      tourist_attraction: 'Attractions',
      other: 'Other Places'
    };

    // Get results for this specific location
    const locationResults = getFilteredResultsForLocation(locationName);
    
    // Group results by category for this location
    const groupedResults = locationResults.reduce((acc, place) => {
      const category = place.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(place);
      return acc;
    }, {});

    // Check if there are any results for this location
    const hasAnyResults = categoryOrder.some(category => {
      const places = groupedResults[category] || [];
      return places.length > 0;
    });

    if (!hasAnyResults) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No places found for {locationName}. Try searching for restaurants, hotels, or attractions.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {categoryOrder.map(category => {
          const places = groupedResults[category] || [];
          console.log(`Category ${category} has ${places.length} places`);
          if (places.length === 0) return null;

          return (
            <Box key={category} sx={{ mb: 3 }}>
              {/* Category Header */}
              <Box 
                onClick={() => toggleSection(category)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 2,
                  pb: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {categories.find(cat => cat.key === category)?.icon}
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: categories.find(cat => cat.key === category)?.color || 'text.primary',
                  flex: 1
                }}>
                  {categoryLabels[category]} ({places.length})
                </Typography>
                {expandedSections[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>

              {/* Places in this category */}
              <Collapse in={expandedSections[category]}>
                {places.map((place) => (
                <Box
                  key={place.id}
                  sx={{
                    py: 1.75,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                    position: 'relative',
                    display: 'flex',
                    gap: 1.5,
                  }}
                >
                  {hasGooglePlacesBrowse && place.photoName ? (
                    <LazyPlacePhoto
                      tripId={trip?.id}
                      photoName={place.photoName}
                      alt={place.name}
                      fallback={<PlaceIcon sx={{ color: DOCUMENT.ink3 }} />}
                    />
                  ) : null}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ ...documentEyebrowSx, mb: 0.5 }}>
                    {categoryLabels[category]}
                  </Typography>
                  <Typography
                    component="a"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name + ' ' + place.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: DOCUMENT.display,
                      fontSize: 18,
                      fontWeight: 400,
                      color: DOCUMENT.ink,
                      textDecoration: 'none',
                      display: 'block',
                      mb: 0.5,
                      '&:hover': { color: DOCUMENT.oxblood },
                    }}
                  >
                    {place.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                    {place.rating ? `${place.rating.toFixed(1)} · ` : ''}
                    {place.address}
                    {' · '}
                    <Box
                      component="button"
                      onClick={(e) => handleAddClick(e, place)}
                      sx={{
                        ...documentTextButtonSx,
                        display: 'inline',
                        cursor: 'pointer',
                        font: 'inherit',
                        fontSize: 12.5,
                        letterSpacing: '0.04em',
                        textTransform: 'none',
                      }}
                    >
                      Add to itinerary
                    </Box>
                  </Typography>
                  </Box>
                </Box>
              ))}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <DocumentSectionHead
        title={t('tripTabs.explore')}
        meta={trip?.destination || trip?.title || ''}
      />
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1, alignItems: 'center', position: 'relative', flex: 1, minWidth: { xs: '100%', md: 280 } }}>
          <Box sx={{ position: 'relative', flex: 1 }}>
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                if (value.trim()) {
                  setShowSuggestions(true);
                  debouncedAutocomplete(value);
                } else {
                  setShowSuggestions(false);
                  setSuggestions([]);
                }
              }}
              onFocus={() => {
                if (suggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for places..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {showSuggestions && suggestions.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                  boxShadow: 3
                }}
              >
                {suggestions.map((suggestion, index) => (
                  <Box
                    key={suggestion.place_id || index}
                    onClick={() => {
                      selectedPredictionRef.current = suggestion;
                      setSearchQuery(suggestion.description);
                      setShowSuggestions(false);
                      setSuggestions([]);
                      setTimeout(() => {
                        handleSearch({ preventDefault: () => {} });
                      }, 100);
                    }}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      borderBottom: index < suggestions.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {suggestion.structured_formatting?.main_text || suggestion.description}
                        </Typography>
                        {suggestion.structured_formatting?.secondary_text && (
                          <Typography variant="caption" color="text.secondary">
                            {suggestion.structured_formatting.secondary_text}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
          <Button
            type="submit"
            disabled={!searchQuery.trim() || isSearching}
            sx={{ ...documentAddButtonSx, mt: 0 }}
          >
            {isSearching ? <CircularProgress size={16} /> : t('common.search')}
          </Button>
          <IconButton onClick={getCurrentLocation} sx={{ color: DOCUMENT.accent }}>
            <MyLocationIcon />
          </IconButton>
        </Box>
        {showResults && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', py: 0.5 }}>
            {categories.map((category) => (
              <Chip
                key={category.key}
                label={category.label}
                onClick={() => {
                  setSelectedCategory(category.key);
                  if (category.key !== 'all' && searchResults.length === 0 && map) {
                    fetchPlacesForLocations(getTripLocations(), { browse: true }).then(({ processedResults, coordMap }) => {
                      setSearchResults(processedResults);
                      setShowResults(true);
                      addMarkersToMap(processedResults);
                      fitMapToCoordinates(map, Array.from(coordMap.values()));
                    }).catch(() => {});
                  }
                }}
                variant="outlined"
                sx={{
                  borderRadius: 0,
                  borderColor: selectedCategory === category.key ? DOCUMENT.accent : DOCUMENT.ruleHard,
                  color: selectedCategory === category.key ? DOCUMENT.accent : DOCUMENT.ink2,
                  bgcolor: 'transparent',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        ref={paneRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: showResults ? '1.4fr 0.9fr' : '1fr' },
          gap: 2.25,
          height: { xs: 'auto', md: paneHeight },
          maxHeight: { md: paneHeight },
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          ref={mapRef}
          sx={{
            ...documentMapBoxSx,
            height: { xs: '50vh', md: '100%' },
            minHeight: { xs: 280, md: 360 },
            width: '100%',
          }}
        />
        {showResults && (
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              height: { xs: 'auto', md: '100%' },
              maxHeight: { xs: '50vh', md: 'none' },
              overflowY: 'auto',
            }}
          >
            {renderResults()}
          </Box>
        )}
      </Box>

      {/* Owner add menu: choose itinerary item or suggestion */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
          }
        }}
      >
        <MenuItem onClick={handleAddAsItinerary}>
          <ListItemIcon><ItineraryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Add to Itinerary</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddAsSuggestion}>
          <ListItemIcon><SuggestionIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Add as Suggestion</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TripMapExplorerNew;
